import { FC, useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { FaPlus } from 'react-icons/fa';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import API from 'core/Api';

import { getSubdomain } from 'utils/helpers/routing';
import roundToFixed from 'utils/helpers/rounding';
import { PaidLeaveDaysByYear, isIntigerOrFloat } from 'utils/helpers/leaves';
import { getUserInfo } from 'utils/auth';
import { updateUserData } from 'utils/userDataSync';

import NotAuthorizedScreen from 'routes/not-authorized';
import Button from 'components/Button';
import MainLayout from 'components/main-layout/MainLayout';
import Loader from 'components/Loader';
import AnnualLeaves from '../components/AnnualLeaves';
import UpcomingLeaves from '../components/UpcomingLeaves';

const Screen: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const createNewLeave = () => history.push('/my-leaves/create');

  const [paidLeaveDays, setPaidLeaveDays] = useState<number>(getUserInfo().paidLeaveDays);
  const [paidLeaveDaysByYear, setPaidLeaveDaysByYear] = useState<PaidLeaveDaysByYear[]>(
    getUserInfo().paidLeaveDaysByYear || []
  );
  const [paidLeaveDaysServedTime, setPaidLeaveDaysServedTime] = useState<number>(
    getUserInfo().paidLeaveDaysServedTime || 0
  );
  const [paidLeaveDaysAdditionalLeave, setPaidLeaveDaysAdditionalLeave] = useState<number>(
    getUserInfo().paidLeaveDaysAdditionalLeave || 0
  );
  const [paidLeaveDaysIrregularWorkingHours, setPaidLeaveDaysIrregularWorkingHours] = useState<number>(
    getUserInfo().paidLeaveDaysIrregularWorkingHours || 0
  );
  const [paidLeaveDaysSpecificWorkingConditions, setPaidLeaveDaysSpecificWorkingConditions] = useState<number>(
    getUserInfo().paidLeaveDaysSpecificWorkingConditions || 0
  );

  const [featureLeaveDaysServedTime, setFeatureLeaveDaysServedTime] = useState(
    getUserInfo().featureLeaveDaysServedTime
  );
  const [featureLeaveDaysAdditionalLeave, setFeatureLeaveDaysAdditionalLeave] = useState(
    getUserInfo().featureLeaveDaysAdditionalLeave
  );
  const [featureLeaveDaysIrregularWorkingHours, setFeatureLeaveDaysIrregularWorkingHours] = useState(
    getUserInfo().featureLeaveDaysIrregularWorkingHours
  );
  const [featureLeaveDaysSpecificWorkingConditions, setFeatureLeaveDaysSpecificWorkingConditions] = useState(
    getUserInfo().featureLeaveDaysSpecificWorkingConditions
  );
  const [featureLimitWfhLeaveDays, setFeatureLimitWfhLeaveDays] = useState(getUserInfo().featureLimitWfhLeaveDays);
  const [featureShowWFHLeaveTypeInMainDropdown, setFeatureShowWFHLeaveTypeInMainDropdown] = useState(
    getUserInfo().featureShowWFHLeaveTypeInMainDropdown
  );
  const [wfhLimitType, setWfhLimitType] = useState(getUserInfo().wfhLimitType);
  const [remainingWfhLeavesPerWeek, setRemainingWfhLeavesPerWeek] = useState(getUserInfo().remainingWfhLeavesPerWeek);
  const [remainingWfhLeavesPerMonth, setRemainingWfhLeavesPerMonth] = useState(
    getUserInfo().remainingWfhLeavesPerMonth
  );
  const [isLeaveModuleActive, setIsLeaveModuleActive] = useState(getUserInfo().featureLeavesIsActive);
  const [isLoading, setIsLoading] = useState(true);

  const isOperator = getSubdomain() === 'operators';

  useEffect(() => {
    /**
     * Update user paid leave days on page load
     */
    updateUserData({
      onSuccess: data => {
        setPaidLeaveDays(data.paid_leave_days);
        setPaidLeaveDaysByYear(data.paid_leave_days_by_year || []);
        setFeatureLeaveDaysServedTime(data.feature_leave_days_served_time_is_active);
        setFeatureLeaveDaysAdditionalLeave(data.feature_paid_additional_leave_is_active);
        setFeatureLeaveDaysIrregularWorkingHours(data.feature_leave_days_irregular_working_hours_is_active);
        setFeatureLeaveDaysSpecificWorkingConditions(data.feature_leave_days_specific_working_conditions_is_active);
        setIsLeaveModuleActive(data.feature_leaves_is_active);
        setPaidLeaveDaysServedTime(roundToFixed(data.paid_leave_served_time_days || 0));
        setPaidLeaveDaysAdditionalLeave(roundToFixed(data.paid_leave_additional_days || 0));
        setPaidLeaveDaysIrregularWorkingHours(
          data.paid_leave_irregular_working_hours || data.paid_leave_irregular_working_hours_days || 0
        );
        setPaidLeaveDaysSpecificWorkingConditions(
          data.paid_leave_specific_working_conditions || data.paid_leave_specific_working_conditions_days || 0
        );
        setWfhLimitType(data.wfh_limit_type);
        setRemainingWfhLeavesPerWeek(data.remaining_wfh_leaves_per_week);
        setRemainingWfhLeavesPerMonth(data.remaining_wfh_leaves_per_month);
        setFeatureLimitWfhLeaveDays(data.feature_limit_wfh_leave_days);
        setFeatureShowWFHLeaveTypeInMainDropdown(data.feature_show_wfh_leave_type_in_main_dropdown);
        setIsLoading(false);
      },
    }).catch(err => {
      console.error(err);
      setIsLoading(false);
    });
  }, []);

  const renderUnusedDaysPaidLeave = (allPaidLeaveDaysByYear: PaidLeaveDaysByYear[]) => {
    // Filter out years with 0 days
    const filteredEntries = allPaidLeaveDaysByYear?.filter(entry => entry.days > 0).sort((a, b) => a.year - b.year);

    return filteredEntries.map((entry, index) => {
      const dayString = entry.days === 1 ? t('leaves.day') : t('leaves.days');

      return (
        <span
          key={index}
          className="d-sm-inline d-md-block d-xl-inline"
          style={{
            marginBottom: '5px',
            fontWeight: 'lighter',
          }}
        >
          {`${index === 0 ? ' (' : ''}${t('leaves.from')} ${entry.year}${t('leaves.yearShort')} - ${isIntigerOrFloat(
            entry.days
          )} ${dayString}${index !== filteredEntries.length - 1 ? ', ' : ')'}`}
        </span>
      );
    });
  };
  if (isLeaveModuleActive || isOperator) {
    return (
      <>
        <MainLayout title={t('leaves.title')}>
          <StyledDiv>
            {isLoading ? (
              <Loader />
            ) : (
              <div>
                <StyledNote>
                  <span className="d-block d-xl-inline">
                    {t('leaves.note')}
                    {paidLeaveDays}
                  </span>
                  {renderUnusedDaysPaidLeave(paidLeaveDaysByYear)}
                </StyledNote>
                {featureLeaveDaysServedTime && (
                  <StyledNote>
                    {t('leaves.noteTimeServed')}
                    {roundToFixed(paidLeaveDaysServedTime)}
                  </StyledNote>
                )}
                {featureLeaveDaysAdditionalLeave && (
                  <StyledNote>
                    {t('leaves.noteAdditionalLeave')}
                    {roundToFixed(paidLeaveDaysAdditionalLeave)}
                  </StyledNote>
                )}
                {featureLeaveDaysIrregularWorkingHours && paidLeaveDaysIrregularWorkingHours > 0 && (
                  <StyledNote>
                    {t('leaves.noteIrregularWorkingHours')}
                    {roundToFixed(paidLeaveDaysIrregularWorkingHours)}
                  </StyledNote>
                )}
                {featureLeaveDaysSpecificWorkingConditions && paidLeaveDaysSpecificWorkingConditions > 0 && (
                  <StyledNote>
                    {t('leaves.noteSpecificWorkingConditions')}
                    {roundToFixed(paidLeaveDaysSpecificWorkingConditions)}
                  </StyledNote>
                )}
                {featureLimitWfhLeaveDays && wfhLimitType !== 'none' && (
                  <StyledNote>
                    {wfhLimitType === 'week'
                      ? `${t('leaves.noteWfhWeekly')}${remainingWfhLeavesPerWeek}`
                      : `${t('leaves.noteWfhMonthly')}${remainingWfhLeavesPerMonth}`}
                  </StyledNote>
                )}
              </div>
            )}
            <Button
              btnType="primary"
              onClick={createNewLeave}
            >
              <FaPlus style={{ marginRight: '0.5rem' }} />
              {t('leaves.addLeaveBtn')}
            </Button>
          </StyledDiv>
          <Row>
            <Col md={8}>
              <UpcomingLeaves />
            </Col>
            <Col md={4}>
              <AnnualLeaves />
            </Col>
          </Row>
        </MainLayout>
      </>
    );
  }

  return <NotAuthorizedScreen />;
};

const StyledDiv = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 2rem;

  @media ${props => props.theme.breakpoints.xl} {
    padding-top: 16px;
    align-items: flex-start;
  }

  @media ${props => props.theme.breakpoints.md} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StyledNote = styled.p`
  font-size: 1rem;
  font-weight: bold;
  align-items: flex-start;
  flex: 1;
  padding-right: 10px;

  @media ${props => props.theme.breakpoints.md} {
    text-align: center;
  }
`;

export default Screen;
